import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dumbbell,
  Folder,
  FolderOpen,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Plus,
  Copy,
  CornerDownRight,
  CornerDownLeft,
  Timer
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import {
  WodElement,
  WodStructure,
  isMovement,
  isGroup,
  WodStructureUtils
} from '../../types/wod.types';

interface StructureNodeProps {
  element: WodElement;
  level: number;
  structure: WodStructure;
  onEdit: (element: WodElement) => void;
  onDelete: (elementId: string) => void;
  onDuplicate: (elementId: string) => void;
  onAddToGroup?: (groupId: string, type: 'movement' | 'group') => void;
  onIndent?: (elementId: string) => void;
  onOutdent?: (elementId: string) => void;
}

export const StructureNode: React.FC<StructureNodeProps> = ({
  element,
  level,
  structure,
  onEdit,
  onDelete,
  onDuplicate,
  onAddToGroup,
  onIndent,
  onOutdent
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const canIndent = WodStructureUtils.canIndent(structure, element.id);
  const canOutdent = WodStructureUtils.canOutdent(structure, element.id);

  const handleAddMovement = () => {
    if (isGroup(element) && onAddToGroup) {
      onAddToGroup(element.id, 'movement');
    }
  };

  const handleAddGroup = () => {
    if (isGroup(element) && onAddToGroup) {
      onAddToGroup(element.id, 'group');
    }
  };

  const indentPx = level * 24;

  // Rendu d'un mouvement
  if (isMovement(element)) {
    return (
      <div
        className="group flex items-center py-2 px-3 bg-muted/50 rounded border border-border mb-1 hover:bg-muted transition-colors"
        style={{
          marginLeft: `${indentPx}px`,
          borderLeftWidth: level > 0 ? '4px' : '1px',
          borderLeftColor: level > 0 ? `hsl(var(--primary) / ${0.3 + level * 0.2})` : undefined
        }}
      >
        {/* Icône mouvement */}
        <Dumbbell className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" />

        {/* Icône tiebreak */}
        {element.isTiebreak && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Timer className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('wods.structureBuilder.tiebreakTooltip')}</p>
              </TooltipContent>
            </Tooltip>
        )}

        {/* Texte du mouvement */}
        <span className="flex-1 font-mono text-sm text-foreground">
          {WodStructureUtils.getMovementDisplayText(element)}
        </span>

        {/* Boutons d'action */}
        <div className="flex gap-0.5 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {canIndent && onIndent && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => onIndent(element.id)}
              title={t('wods.structureBuilder.indent')}
            >
              <CornerDownRight className="h-4 w-4" />
            </Button>
          )}
          {canOutdent && onOutdent && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => onOutdent(element.id)}
              title={t('wods.structureBuilder.outdent')}
            >
              <CornerDownLeft className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => onDuplicate(element.id)}
            title={t('wods.structureBuilder.duplicate')}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => onEdit(element)}
            title={t('wods.structureBuilder.edit')}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(element.id)}
            title={t('wods.structureBuilder.deleteAction')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Rendu d'un groupe
  if (isGroup(element)) {
    const ladder = WodStructureUtils.ladderConfig(element);
    const groupLabel = ladder
      ? `Ladder ${WodStructureUtils.ladderDescription(ladder)}${element.name ? ` (${element.name})` : ''}`
      : element.isAmrap
        ? `AMRAP${element.name ? ` (${element.name})` : ''}`
        : `${element.repetitions} Round${element.repetitions > 1 ? 's' : ''}${element.name ? ` (${element.name})` : ''}`;

    return (
      <div className="mb-1">
        {/* Header du groupe */}
        <Droppable droppableId={`group-${element.id}`} type="WOD_ELEMENT">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`group flex items-center py-2 px-3 rounded transition-colors ${
                snapshot.isDraggingOver
                  ? 'bg-white/[0.08] border-2 border-foreground/60'
                  : 'bg-white/[0.04] border border-white/[0.14] hover:bg-white/[0.06]'
              }`}
              style={{
                marginLeft: `${indentPx}px`,
                borderLeftWidth: level > 0 ? '4px' : '1px',
                borderLeftColor: level > 0 ? `rgba(255, 255, 255, ${0.14 + level * 0.1})` : undefined
              }}
            >
              {/* Bouton expand/collapse */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 mr-1 text-muted-foreground hover:text-foreground"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {/* Icône groupe */}
              {expanded ? (
                <FolderOpen className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" />
              ) : (
                <Folder className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" />
              )}

              {/* Label du groupe */}
              <span className="flex-1 font-semibold font-mono text-sm text-foreground">
                {groupLabel}
              </span>

              {/* Badge nombre d'enfants */}
              <span className="px-2 py-0.5 bg-white/10 text-muted-foreground text-xs rounded mr-2 font-mono">
                {t('wods.structureBuilder.elements', { count: element.children.length })}
              </span>

              {/* Boutons d'action */}
              <div className="flex gap-0.5 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                {canIndent && onIndent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => onIndent(element.id)}
                    title={t('wods.structureBuilder.indent')}
                  >
                    <CornerDownRight className="h-4 w-4" />
                  </Button>
                )}
                {canOutdent && onOutdent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => onOutdent(element.id)}
                    title={t('wods.structureBuilder.outdent')}
                  >
                    <CornerDownLeft className="h-4 w-4" />
                  </Button>
                )}

                {/* Menu d'ajout */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      title={t('wods.structureBuilder.add')}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleAddMovement} className="cursor-pointer">
                      <Dumbbell className="h-4 w-4 mr-2" />
                      {t('wods.structureBuilder.addSubMovement')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleAddGroup} className="cursor-pointer">
                      <Folder className="h-4 w-4 mr-2" />
                      {t('wods.structureBuilder.addSubGroup')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => onDuplicate(element.id)}
                  title={t('wods.structureBuilder.duplicate')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => onEdit(element)}
                  title={t('wods.structureBuilder.edit')}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(element.id)}
                  title={t('wods.structureBuilder.deleteAction')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {/* Enfants du groupe (avec animation) */}
        <div
          className={`mt-0.5 grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
            expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="min-h-0 overflow-hidden">
          {element.children.length === 0 ? (
            <div
              className="py-4 px-3 text-muted-foreground italic text-sm"
              style={{ marginLeft: `${indentPx + 24}px` }}
            >
              {t('wods.structureBuilder.emptyGroup')}
            </div>
          ) : (
            <Droppable droppableId={`group-children-${element.id}`} type="WOD_ELEMENT">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`transition-colors duration-200 ${
                    snapshot.isDraggingOver ? 'bg-white/[0.05]' : ''
                  }`}
                >
                  {element.children.map((child, index) => (
                    <Draggable key={child.id} draggableId={child.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.9 : 1,
                            transform: snapshot.isDragging
                              ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                              : provided.draggableProps.style?.transform,
                          }}
                          className={snapshot.isDragging ? 'shadow-lg shadow-primary/50' : ''}
                        >
                          <StructureNode
                            element={child}
                            level={level + 1}
                            structure={structure}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onDuplicate={onDuplicate}
                            onAddToGroup={onAddToGroup}
                            onIndent={onIndent}
                            onOutdent={onOutdent}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
